// Pure, viewer-agnostic builders for the unified Browser pane's Locations/Systems trees.
//
// Extracted verbatim from AutodeskViewer (the only viewer that had the inline hierarchy pane)
// so EVERY viewer can build the same tree from generic POI data. There is no viewer/SVF2
// coupling here: callers inject the POI field-readers, all of which are thin wrappers over the
// canonical StoreyClassifier / POIFieldExtractor helpers and read plain POI fields only. The
// 3D-specific "Show in 3D" target (dbId/sourceFile) is NOT part of tree building — it stays a
// callback on the consuming component.

export interface HierarchyNode {
  id: string;
  name: string;
  type: 'storey' | 'space' | 'poi' | 'category' | 'subcategory' | 'system';
  children: HierarchyNode[];
  poi?: any;
  count?: number;
  // Space nodes: room element dbId and source file (for the 3D "Room" button)
  roomDbId?: number;
  sourceFile?: string;
  // Custom rooms: polygon data for visualization
  polygon?: { points: { x: number; y: number }[]; minZ: number; maxZ: number };
  isCustom?: boolean;
  // Space nodes carry the room's elevation/height when the joined location is a real Space.
  // (The original inline interface omitted these even though the code set them — the omission
  //  was one of the tolerated baseline type errors; declaring them here is strictly better.)
  elevation?: number;
  height?: number;
}

// POI field-readers the Locations builder needs. Each is a plain function over a raw POI row;
// AutodeskViewer supplies its memoized wrappers, other viewers supply the same shared-backed ones.
export interface LocationsHierarchyHelpers {
  getPOIStorey: (poi: any) => string;
  getPOISpace: (poi: any) => string | null;
  getPOIName: (poi: any) => string;
  isSpaceType: (type: string | null) => boolean;
}

// Minimal shape of a custom room row consumed by the Locations builder.
export interface CustomRoomInput {
  id: string | number;
  name: string;
  parent_name?: string;
  elevation?: number;
  height?: number;
  geometry_polygon?: { points: { x: number; y: number }[]; minZ: number; maxZ: number } | null;
}

// Build POI hierarchy tree for Locations (storey → space → POIs).
export function buildLocationsHierarchy(
  allPois: any[],
  customRooms: CustomRoomInput[],
  helpers: LocationsHierarchyHelpers,
): HierarchyNode[] {
  const { getPOIStorey, getPOISpace, getPOIName, isSpaceType } = helpers;

  const storeyMap = new Map<string, HierarchyNode>();

  allPois.forEach(poi => {
    const storeyName = getPOIStorey(poi);

    // FIX: Use poi.locations array (populated by loadPOIsForAsset aggregation) to add POI to ALL its assigned rooms
    // This matches the behavior of Locations page which shows POIs under all their assigned rooms
    const locationsToProcess: Array<{ name: string; level_name?: string; ifc_id?: number; source_file?: string; elevation?: number; height?: number }> = [];

    if (poi.locations && Array.isArray(poi.locations) && poi.locations.length > 0) {
      // Use the aggregated locations array - POI will appear under each room it's assigned to
      poi.locations.forEach((loc: any) => {
        if (loc.type === 'Spaces' && loc.name) {
          locationsToProcess.push({
            name: loc.name,
            level_name: loc.level_name,
            ifc_id: loc.ifc_id,
            source_file: loc.source_file,
            elevation: loc.elevation,
            height: loc.height
          });
        }
      });
    }

    // Fallback to original behavior if no locations array or no Spaces locations.
    // Identity fields (ifc_id/source_file/elevation/height) only carry over when the
    // POI's joined location really is a Space — for storey-assigned POIs the name comes
    // from metadata and location_ifc_id is the STOREY's dbId; stamping it here made every
    // Room button under a storey navigate to that one wrong element (2026-07 staging bug).
    // Without ifc_id the node keeps grouping POIs but the Room button self-hides.
    if (locationsToProcess.length === 0) {
      const spaceName = getPOISpace(poi);
      if (spaceName) {
        const locationIsSpace = isSpaceType(poi.location_type);
        locationsToProcess.push({
          name: spaceName,
          ifc_id: locationIsSpace ? poi.location_ifc_id : undefined,
          source_file: locationIsSpace ? (poi.location_source_file || poi.ifc_source_file) : undefined,
          elevation: locationIsSpace ? poi.location_elevation : undefined,
          height: locationIsSpace ? poi.location_height : undefined
        });
      }
    }

    if (!storeyMap.has(storeyName)) {
      storeyMap.set(storeyName, {
        id: `storey-${storeyName}`,
        name: storeyName,
        type: 'storey',
        children: [],
        count: 0
      });
    }

    const storeyNode = storeyMap.get(storeyName)!;
    storeyNode.count = (storeyNode.count || 0) + 1;

    if (locationsToProcess.length > 0) {
      // Add POI to each room it's assigned to
      locationsToProcess.forEach(location => {
        const spaceName = location.name;
        let spaceNode = storeyNode.children.find(c => c.type === 'space' && c.name === spaceName);
        if (!spaceNode) {
          // Get room dbId from location's ifc_id
          const roomDbId = location.ifc_id ? Number(location.ifc_id) : undefined;
          const sourceFile = location.source_file || poi.ifc_source_file || '';
          spaceNode = {
            id: `space-${storeyName}-${spaceName}`,
            name: spaceName,
            type: 'space',
            children: [],
            count: 0,
            roomDbId,
            sourceFile,
            elevation: location.elevation,
            height: location.height
          };
          storeyNode.children.push(spaceNode);
        }
        // UPDATE roomDbId if this location has authoritative space data
        if (location.ifc_id) {
          spaceNode.roomDbId = Number(location.ifc_id);
          spaceNode.sourceFile = location.source_file || spaceNode.sourceFile;
          spaceNode.elevation = location.elevation ?? spaceNode.elevation;
          spaceNode.height = location.height ?? spaceNode.height;
        }
        spaceNode.count = (spaceNode.count || 0) + 1;
        spaceNode.children.push({
          id: `poi-${poi.id || poi.poi_id}-in-${spaceName}`,
          name: getPOIName(poi),
          type: 'poi',
          children: [],
          poi
        });
      });
    } else {
      // No room assignment - add directly to storey
      storeyNode.children.push({
        id: `poi-${poi.id || poi.poi_id}`,
        name: getPOIName(poi),
        type: 'poi',
        children: [],
        poi
      });
    }
  });

  // Sort storeys (Unassigned at end) and sort children within each storey (spaces first, then POIs)
  const sortedStoreys = Array.from(storeyMap.values()).sort((a, b) => {
    if (a.name === 'Unassigned') return 1;
    if (b.name === 'Unassigned') return -1;
    return a.name.localeCompare(b.name);
  });

  // Sort children of each storey: spaces (rooms) first, then direct POIs
  sortedStoreys.forEach(storey => {
    storey.children.sort((a, b) => {
      // Spaces always come before POIs
      if (a.type === 'space' && b.type !== 'space') return -1;
      if (a.type !== 'space' && b.type === 'space') return 1;
      // Among spaces, sort by count (most POIs first)
      if (a.type === 'space' && b.type === 'space') {
        return (b.count || 0) - (a.count || 0);
      }
      // Among POIs, sort alphabetically by name
      return a.name.localeCompare(b.name);
    });
  });

  // Inject custom rooms into the hierarchy
  if (customRooms.length > 0) {
    customRooms.forEach(room => {
      const polygon = room.geometry_polygon;
      // Find parent storey by name or parent_id
      let parentStorey = sortedStoreys.find(s =>
        s.name === room.parent_name || s.id === `storey-${room.parent_name}`
      );

      // If no matching storey found, try to find by the parent location's name
      if (!parentStorey) {
        // Create a "Custom Rooms" storey node if needed
        let customStorey = sortedStoreys.find(s => s.id === 'storey-Custom Rooms');
        if (!customStorey) {
          customStorey = {
            id: 'storey-Custom Rooms',
            name: 'Custom Rooms',
            type: 'storey' as const,
            children: [],
            count: 0
          };
          // Insert before "Unassigned"
          const unassignedIdx = sortedStoreys.findIndex(s => s.name === 'Unassigned');
          if (unassignedIdx >= 0) {
            sortedStoreys.splice(unassignedIdx, 0, customStorey);
          } else {
            sortedStoreys.push(customStorey);
          }
        }
        parentStorey = customStorey;
      }

      if (parentStorey) {
        // Check if a space node with this name already exists (created from POI locations)
        const existingSpace = parentStorey.children.find(
          c => c.type === 'space' && c.name === room.name
        );
        if (existingSpace) {
          // Upgrade existing node with custom room data (adds Room button + Custom badge)
          existingSpace.isCustom = true;
          existingSpace.polygon = polygon || undefined;
          existingSpace.elevation = room.elevation ?? existingSpace.elevation;
          existingSpace.height = room.height ?? existingSpace.height;
        } else if (!parentStorey.children.some(c => c.id === `custom-room-${room.id}`)) {
          // No existing node — create new custom room entry
          parentStorey.children.push({
            id: `custom-room-${room.id}`,
            name: room.name,
            type: 'space',
            children: [],
            count: 0,
            polygon: polygon || undefined,
            isCustom: true,
            elevation: room.elevation,
            height: room.height
          });
        }
      }
    });

    // Re-sort affected storeys so custom rooms appear with other spaces (before POIs)
    sortedStoreys.forEach(storey => {
      storey.children.sort((a, b) => {
        if (a.type === 'space' && b.type !== 'space') return -1;
        if (a.type !== 'space' && b.type === 'space') return 1;
        if (a.type === 'space' && b.type === 'space') {
          return (b.count || 0) - (a.count || 0);
        }
        return a.name.localeCompare(b.name);
      });
    });
  }

  return sortedStoreys;
}

// Build POI hierarchy tree for Systems matching SystemsPage structure.
// Category → SubCategory (levels[0]) → System (levels[1]) → POIs
export function buildSystemsHierarchy(
  allPois: any[],
  getPOIName: (poi: any) => string,
): HierarchyNode[] {
  // SYSTEMS ARE A TREE AGAIN (migration 058). An asset attaches to the DEEPEST system it
  // belongs to; every level above it is an ancestor, and membership ROLLS UP — an asset in
  // HVAC > Chillers > CH-1 appears under all three.
  //
  // The path comes from the legacy mirror, which systemsRegistry rewrites on every assignment:
  // system_category is the root and system_subcategory a JSON array of the names beneath it.
  // Reading it here means the Browser pane needs no extra fetch of the systems table.
  //
  // AN ASSET CAN BELONG TO MANY SYSTEMS, so the same asset legitimately appears under each.
  // That is the model, not a duplicate bug.
  const roots = new Map<string, HierarchyNode>();

  /**
   * EVERY path this asset sits on. Usually one — the primary system's chain from the mirror —
   * but an asset may belong to several systems, and each of those is its own path. The mirror
   * can only describe the primary's ancestry, so the extra junction systems come in as roots.
   */
  const pathsOf = (poi: any): string[][] => {
    const paths: string[][] = [];

    const root = String(poi.system_category ?? poi.ifc_category ?? '').trim();
    if (root) {
      const raw = poi.system_subcategory;
      let below: unknown[] = [];
      if (Array.isArray(raw)) below = raw;
      else if (typeof raw === 'string' && raw.trim() !== '') {
        try {
          const parsed = JSON.parse(raw);
          below = Array.isArray(parsed) ? parsed : [raw];
        } catch { below = [raw]; }   // a pre-039 row holds a bare string
      }
      paths.push([root, ...below.map((v) => String(v ?? '').trim()).filter(Boolean)]);
    }

    const junction = Array.isArray(poi.systems)
      ? poi.systems.map((sys: any) => String((typeof sys === 'string' ? sys : sys?.name) ?? '').trim()).filter(Boolean)
      : [];
    // Anything the mirror's path already accounts for is not a second membership.
    const covered = new Set(paths.flat().map((n) => n.toLowerCase()));
    for (const systemName of junction) {
      if (!covered.has(systemName.toLowerCase())) paths.push([systemName]);
    }

    // No system at all. Named rather than dropped: an asset that fell out of every system is
    // exactly what someone opens this pane to find.
    return paths.length > 0 ? paths : [['Unassigned']];
  };

  allPois.forEach(poi => {
    for (const path of pathsOf(poi)) {
      let siblings = roots;
      let node: HierarchyNode | undefined;
      let idPrefix = 'system';

      for (const name of path) {
        idPrefix = `${idPrefix}-${name}`;
        let child = siblings.get(name);
        if (!child) {
          child = { id: idPrefix, name, type: 'system', children: [], count: 0 };
          siblings.set(name, child);
        }
        // Every level on the path gets the asset counted: the number on a parent means
        // "assets attached at or below here", which is what makes a parent row meaningful.
        child.count = (child.count || 0) + 1;
        node = child;
        // Children keep their own name map, hung off the node so the walk stays O(depth)
        // instead of scanning children at each level.
        const childMap = (child as any).__childMap || new Map<string, HierarchyNode>();
        (child as any).__childMap = childMap;
        siblings = childMap;
      }

      if (node) {
        node.children.push({
          id: `poi-${poi.id || poi.poi_id}-${node.id}`,
          name: getPOIName(poi),
          type: 'poi',
          children: [],
          poi,
        });
      }
    }
  });

  // Splice each level's system children in ahead of its POIs, sorted by name, and drop the
  // scratch map so the returned nodes are plain data.
  const finish = (map: Map<string, HierarchyNode>): HierarchyNode[] => {
    const list = Array.from(map.values());
    for (const node of list) {
      const childMap: Map<string, HierarchyNode> | undefined = (node as any).__childMap;
      delete (node as any).__childMap;
      const systems = childMap ? finish(childMap) : [];
      // Systems first, then the assets attached at this level — a parent's own assets read
      // as "the rest", below the named groups.
      node.children = [...systems, ...node.children];
    }
    return list.sort((a, b) => {
      if (a.name === 'Unassigned') return 1;
      if (b.name === 'Unassigned') return -1;
      return a.name.localeCompare(b.name);
    });
  };

  return finish(roots);
}


// Recursively collect all POIs from a hierarchy node (used by group-select / bulk actions).
export function collectPOIsFromNode(node: HierarchyNode | any): any[] {
  const pois: any[] = [];
  if (node.poi) {
    pois.push(node.poi);
  }
  if (node.children) {
    for (const child of node.children) {
      pois.push(...collectPOIsFromNode(child));
    }
  }
  return pois;
}
